import React, { useState, useRef } from 'react';
import {
  View,
  Modal,
  ActivityIndicator,
  Alert,
  SafeAreaView,
  Platform,
} from 'react-native';
import { useTheme } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { useNavigation } from '@react-navigation/native';
import QText from './QText';
import Button from './Button';
import { getToken } from '../utils';
import { navigationRef } from '../navigations/NavigationService';

let WebView;
if (Platform.OS === 'web') {
  WebView = null;
} else {
  try {
    const webViewModule = require('react-native-webview');
    WebView = webViewModule.WebView;
  } catch (e) {
    WebView = null;
  }
}

const PaymentWebViewModal = ({
  visible,
  paymentUrl,
  onSuccess,
  onFailure,
  onClose,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  // Make navigation optional - only use if available
  let navigation = null;
  try {
    navigation = useNavigation();
    // Check if navigation object is valid
    if (navigation && typeof navigation.navigate === 'function') {
      // Navigation object is valid
    } else {
      navigation = null;
    }
  } catch (error) {
    // Navigation not available, continue without it
    navigation = null;
  }
  const [isLoading, setIsLoading] = useState(true);
  const [canGoBack, setCanGoBack] = useState(false);
  const webViewRef = useRef(null);

  const handleNavigationStateChange = (navState) => {
    setCanGoBack(navState.canGoBack);

    const { url } = navState;

    if (
      url.includes('success') ||
      url.includes('payment-success') ||
      url.includes('transaction-success') ||
      url.includes('status=success')
    ) {
      onSuccess();
      return;
    }

    if (
      url.includes('failure') ||
      url.includes('cancel') ||
      url.includes('error') ||
      url.includes('status=failed') ||
      url.includes('status=cancelled')
    ) {
      onFailure();
      return;
    }
  };

  const handleLoadStart = () => {
    setIsLoading(true);
  };

  const handleLoadEnd = () => {
    setIsLoading(false);
  };

  const handleError = (syntheticEvent) => {
    const { nativeEvent } = syntheticEvent;
    setIsLoading(false);
    Alert.alert(t('error'), t('payment_webview_error'), [
      {
        text: t('ok'),
        onPress: onFailure,
      },
    ]);
  };

  const handleIframeLoad = () => {
    setIsLoading(false);
  };

  const handleIframeError = () => {
    setIsLoading(false);
    Alert.alert(t('error'), t('payment_webview_error'), [
      {
        text: t('ok'),
        onPress: onFailure,
      },
    ]);
  };

  const handleBackPress = () => {
    if (canGoBack && webViewRef.current) {
      if (Platform.OS === 'web') {
        Alert.alert(t('cancel_payment'), t('are_you_sure_cancel_payment'), [
          { text: t('no'), style: 'cancel' },
          { text: t('yes'), onPress: onClose },
        ]);
      } else {
        webViewRef.current.goBack();
      }
    } else {
      Alert.alert(t('cancel_payment'), t('are_you_sure_cancel_payment'), [
        { text: t('no'), style: 'cancel' },
        { text: t('yes'), onPress: onClose },
      ]);
    }
  };

  const handleGoToDashboard = async () => {
    onClose();

    // Check auth token first to determine the right action
    const token = await getToken();

    // Try to use navigationRef first (more reliable for cross-stack navigation)
    if (navigationRef.current && navigationRef.current.navigate) {
      if (token) {
        // User is authenticated, try to go to dashboard
        try {
          navigationRef.current.navigate('DashboardScreen');
          return;
        } catch (error) {
          // Continue to fallback navigation
        }
      } else {
        // User is not authenticated, redirect to sign in
        try {
          navigationRef.current.navigate('SignInScreen');
          return;
        } catch (signInError) {
          // Continue to fallback navigation
        }
      }
    }

    // Fallback to local navigation object if navigationRef fails
    if (navigation && typeof navigation.navigate === 'function') {
      if (token) {
        // User is authenticated, try to go to dashboard
        try {
          navigation.navigate('DashboardScreen');
        } catch (error) {
          try {
            navigation.navigate('Dashboard');
          } catch (fallbackError) {
            onSuccess();
          }
        }
      } else {
        // User is not authenticated, redirect to sign in
        try {
          // First try SignInScreen (which exists in GuestNavigation)
          navigation.navigate('SignInScreen');
        } catch (signInError) {
          // If SignInScreen fails, try alternative sign-in routes
          try {
            navigation.navigate('SignIn');
          } catch (altSignInError) {
            onSuccess();
          }
        }
      }
    } else {
      // No navigation available
      if (token) {
        // User is authenticated but no navigation, call onSuccess
        onSuccess();
      } else {
        // User is not authenticated and no navigation, call onSuccess as fallback
        onSuccess();
      }
    }
  };

  const styles = {
    modalContainer: {
      flex: 1,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
    },
    modalContentWithButton: {
      flex: 1,
      backgroundColor: theme.colors.background,
    },
    paymentArea: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 20,
    },
    webViewContainer: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
    },
    iframe: {
      width: '400px',
      maxWidth: '90vw',
      height: '500px',
      maxHeight: '70vh',
      border: 'none',
      borderRadius: '12px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
    },
    loadingContainer: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: theme.colors.background,
    },
    loadingText: {
      marginTop: 12,
      color: theme.colors.onBackground,
    },
    bottomButtonContainer: {
      padding: Platform.OS === 'web' ? 20 : 16,
      paddingHorizontal: Platform.OS === 'web' ? 40 : 20,
      alignItems: 'center',
      backgroundColor: theme.colors.surface || theme.colors.background,
      borderTopWidth: 1,
      borderTopColor: theme.colors.outline || '#E0E0E0',
      minHeight: Platform.OS === 'web' ? 120 : 100,
      justifyContent: 'center',
    },
    noteText: {
      fontSize: Platform.OS === 'web' ? 14 : 13,
      color: theme.colors.secondaryText || theme.colors.onSurface,
      textAlign: 'center',
      marginBottom: Platform.OS === 'web' ? 16 : 12,
      lineHeight: Platform.OS === 'web' ? 20 : 18,
      paddingHorizontal: 10,
      maxWidth: Platform.OS === 'web' ? 500 : '100%',
    },
    dashboardButton: {
      width: '100%',
      maxWidth: '400px',
      minWidth: '300px',
      height: 48,
    },
    dashboardButtonNative: {
      width: '100%',
      maxWidth: 400,
      minWidth: 300,
      height: 48,
    },
  };

  if (!paymentUrl || paymentUrl.trim() === '') {
    return null;
  }

  const renderWebContent = () => {
    return (
      <div style={styles.webViewContainer}>
        <iframe
          src={paymentUrl}
          style={styles.iframe}
          onLoad={handleIframeLoad}
          onError={handleIframeError}
          title="Payment Gateway"
        />

        {isLoading && (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={theme.colors.primary} />
            <QText style={styles.loadingText}>{t('loading_payment')}</QText>
          </View>
        )}
      </div>
    );
  };

  const renderNativeContent = () => {
    if (!WebView) {
      return (
        <View style={styles.loadingContainer}>
          <QText style={styles.loadingText}>WebView not available</QText>
        </View>
      );
    }

    return (
      <View style={styles.webViewContainer}>
        <WebView
          ref={webViewRef}
          source={{ uri: paymentUrl }}
          onNavigationStateChange={handleNavigationStateChange}
          onLoadStart={handleLoadStart}
          onLoadEnd={handleLoadEnd}
          onError={handleError}
          startInLoadingState={true}
          javaScriptEnabled={true}
          domStorageEnabled={true}
          allowsBackForwardNavigationGestures={true}
          userAgent={
            Platform.OS === 'ios'
              ? 'Mozilla/5.0 (iPhone; CPU iPhone OS 14_7_1 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/14.1.2 Mobile/15E148 Safari/604.1'
              : undefined
          }
          mixedContentMode="compatibility"
          allowsInlineMediaPlayback={true}
          mediaPlaybackRequiresUserAction={false}
          style={{ flex: 1 }}
        />

        {isLoading && (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={theme.colors.primary} />
            <QText style={styles.loadingText}>{t('loading_payment')}</QText>
          </View>
        )}
      </View>
    );
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="fullScreen"
      onRequestClose={handleBackPress}
    >
      <SafeAreaView style={styles.modalContainer}>
        <View style={styles.modalContentWithButton}>
          <View style={styles.paymentArea}>
            {Platform.OS === 'web' ? renderWebContent() : renderNativeContent()}
          </View>

          {navigation && (
            <View style={styles.bottomButtonContainer}>
              <QText style={styles.noteText}>
                {t('if_payment_not_redirected_click_below')}
              </QText>
              <Button
                mode="contained"
                onPress={handleGoToDashboard}
                style={
                  Platform.OS === 'web'
                    ? styles.dashboardButton
                    : styles.dashboardButtonNative
                }
              >
                {t('go_to_dashboard')}
              </Button>
            </View>
          )}
        </View>
      </SafeAreaView>
    </Modal>
  );
};

export default PaymentWebViewModal;
